import React, { useEffect, useState } from "react";
import { HiArrowLeft } from "react-icons/hi";
import { GoalStatus } from "@pages/Program/utils/enums";
import { MilestoneDisplay } from "@shared/interfaces/ProgramFiles";
import MilestoneCard from "./MilestoneCard";
import { useProgram } from "@pages/Program/utils/useProgram";
import { useParams } from "react-router-dom";
import EditMilestone from "./EditMilestone";
import DeleteEntityModal from "../DeleteEntity";
import { useMilestone } from "./utils/useMilestone";
import Pill from "@shared/components/Pill";
import TruncatedNameWithTooltip from "@shared/components/TruncatedNameWithTooltip";

interface MilestonesModalProps {
  title?: string;
  milestones: MilestoneDisplay[];
  onClose: () => void;
  allowEdit?: boolean;
  singleMilestone?: boolean;
  startDate?: string;
  endDate?: string;
  fetchMilestone?: () => void;
}

const statusColorMap: Record<MilestoneDisplay["status"], string> = {
  on_track: "text-[#009947] border-[#009947] bg-[#EAFBF2] border",
  behind: "text-[#F29F04] border-[#F29F04] bg-[#FFF6E6] border",
  at_risk: "text-[#F04438] border-[#F04438] bg-[#FEF3F2] border",
};

const MilestonesModal: React.FC<MilestonesModalProps> = ({
  title,
  milestones,
  onClose,
  singleMilestone,
  allowEdit = false,
  startDate,
  endDate,
  fetchMilestone,
}) => {
  const [selectedMilestone, setSelectedMilestone] =
    useState<MilestoneDisplay | null>(singleMilestone ? milestones[0] : null);

  const [isEditMilestoneOpen, setIsEditMilestoneOpen] = useState(false);

  const handleBack = () => setSelectedMilestone(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { deleteMilestone } = useMilestone();

  const { getProgramUpdatesByType } = useProgram();

  const { programId } = useParams();

  const [updates, setUpdates] = useState<any[]>([]);

  const handleDeleteMilestone = async () => {
    if (!selectedMilestone || !fetchMilestone) return;
    try {
      await deleteMilestone(selectedMilestone.id);
      fetchMilestone();
    } catch {
      console.error("Failed to delete milestone");
    }
    setModalOpen(false);
    setSelectedMilestone({} as MilestoneDisplay);
  };

  useEffect(() => {
    if (!programId) return;
    (async () => {
      const data = await getProgramUpdatesByType({
        programId: programId,
        type: "milestone",
        ids: milestones.map((m) => m.id),
      });
      setUpdates(data);
    })();
  }, []);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[99]">
      <div className="bg-white rounded-lg w-full max-w-2xl min-h-[20rem] shadow-lg max-h-[95vh] flex flex-col">
        <div className="flex items-center border-b px-6 py-4 space-x-2">
          {selectedMilestone && !singleMilestone && (
            <button
              onClick={handleBack}
              className="hover:bg-[#efefef] p-1 rounded-full"
            >
              <HiArrowLeft className="w-5 h-5 text-gray-400" />
            </button>
          )}
          {selectedMilestone ? (
            <div className="flex w-full justify-between items-center">
              <TruncatedNameWithTooltip
                name={selectedMilestone.name}
                widthClass="w-full"
                textClass="text-lg font-semibold"
              />
              <div className="whitespace-nowrap">
                <Pill
                  styling={`${statusColorMap[selectedMilestone.status]}`}
                  text={GoalStatus[selectedMilestone.status]}
                />
              </div>
            </div>
          ) : (
            <h2 className="text-lg font-semibold">
              {`Tasks${title ? ` - ${title}` : ""}`}
            </h2>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {milestones.length === 0 ? (
            <p className="text-gray-500 text-sm flex items-center justify-center py-8 mt-16">
              No milestones are currently associated with this goal.
            </p>
          ) : selectedMilestone ? (
            <div className="px-6 py-4">
              <MilestoneCard
                milestone={selectedMilestone}
                detailed
                updates={
                  updates.filter(
                    (update) => update.target_id === selectedMilestone.id
                  )[0]?.updates || []
                }
              />
            </div>
          ) : (
            milestones.map((milestone, idx) => (
              <div key={idx} className="mx-6 mt-4">
                <MilestoneCard
                  milestone={milestone}
                  onClick={() => setSelectedMilestone(milestone)}
                  updates={
                    updates.filter(
                      (update) => update.target_id === milestone.id
                    )[0]?.updates || []
                  }
                />
              </div>
            ))
          )}
        </div>

        <div className="flex justify-end px-6 py-4 space-x-2">
          <button
            onClick={onClose}
            className="text-sm text-blue-600 border-blue-600 border rounded-md px-4 py-2 transition duration-200 ease-in-out"
          >
            Close
          </button>
          {selectedMilestone && allowEdit && (
            <button
              onClick={() => setIsEditMilestoneOpen(true)}
              className="text-sm bg-blue-600 text-white border-blue-600 hover:bg-blue-700 border rounded-md px-4 py-2 "
            >
              Edit
            </button>
          )}
        </div>

        {startDate &&
          endDate &&
          selectedMilestone &&
          fetchMilestone &&
          allowEdit && (
            <EditMilestone
              milestoneId={selectedMilestone.id}
              isOpen={isEditMilestoneOpen}
              fetchMilestone={fetchMilestone}
              onClose={() => setIsEditMilestoneOpen(false)}
              startDate={startDate}
              endDate={endDate}
              onDelete={() => setModalOpen(true)}
            />
          )}

        <DeleteEntityModal
          isOpen={modalOpen}
          selectedEntity={selectedMilestone}
          onClose={() => setModalOpen(false)}
          onConfirm={handleDeleteMilestone}
        />
      </div>
    </div>
  );
};

export default MilestonesModal;
